/*!
 * @jscrypto/paddings v0.9.1
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
import { assertBlockSize as assertBlockSize$3, assertPaddedInput as assertPaddedInput$3, getBlockPaddingLength, assertBlockMultiple } from '@jscrypto/core';

const ansiX923 = {
    kind: 'padding',
    name: 'AnsiX923',
    pad(input, blockSize) {
        assertBlockSize$2(blockSize);
        const paddingLength = getPaddingLength$2(input.length, blockSize);
        const output = new Uint8Array(input.length + paddingLength);
        output.set(input);
        output[output.length - 1] = paddingLength;
        return output;
    },
    unpad(input, blockSize) {
        assertPaddedInput$2(input, blockSize);
        const paddingLength = input[input.length - 1];
        if (paddingLength === 0 || paddingLength > blockSize || paddingLength > input.length) {
            throw new Error('Invalid ANSI X9.23 padding.');
        }
        for (let i = input.length - paddingLength; i < input.length - 1; i++) {
            if (input[i] !== 0) {
                throw new Error('Invalid ANSI X9.23 padding.');
            }
        }
        return input.slice(0, input.length - paddingLength);
    },
};
const ansiX923Preset = {
    kind: 'preset',
    name: 'ansi-x923',
    components() {
        return [ansiX923];
    },
};
function assertBlockSize$2(blockSize) {
    assertBlockSize$3(blockSize, { max: 255 });
}
function assertPaddedInput$2(input, blockSize) {
    assertPaddedInput$3(input, blockSize, 'ANSI X9.23', { max: 255 });
}
function getPaddingLength$2(inputLength, blockSize) {
    return getBlockPaddingLength(inputLength, blockSize);
}

const iso10126 = {
    kind: 'padding',
    name: 'Iso10126',
    pad(input, blockSize) {
        assertBlockSize$1(blockSize);
        const paddingLength = getPaddingLength$1(input.length, blockSize);
        const output = new Uint8Array(input.length + paddingLength);
        output.set(input);
        fillRandom(output.subarray(input.length, output.length - 1));
        output[output.length - 1] = paddingLength;
        return output;
    },
    unpad(input, blockSize) {
        assertPaddedInput$1(input, blockSize);
        const paddingLength = input[input.length - 1];
        if (paddingLength === 0 || paddingLength > blockSize || paddingLength > input.length) {
            throw new Error('Invalid ISO 10126 padding.');
        }
        return input.slice(0, input.length - paddingLength);
    },
};
const iso10126Preset = {
    kind: 'preset',
    name: 'iso10126',
    components() {
        return [iso10126];
    },
};
function assertBlockSize$1(blockSize) {
    assertBlockSize$3(blockSize, { max: 255 });
}
function assertPaddedInput$1(input, blockSize) {
    assertPaddedInput$3(input, blockSize, 'ISO 10126', { max: 255 });
}
function getPaddingLength$1(inputLength, blockSize) {
    return getBlockPaddingLength(inputLength, blockSize);
}
function fillRandom(bytes) {
    if (bytes.length === 0) {
        return;
    }
    const crypto = globalThis;
    crypto.crypto?.getRandomValues(bytes);
    if (bytes.some((byte) => byte !== 0)) {
        return;
    }
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
    }
}

const iso97971 = {
    kind: 'padding',
    name: 'Iso97971',
    pad(input, blockSize) {
        assertBlockSize(blockSize);
        const paddingLength = getPaddingLength(input.length, blockSize);
        const output = new Uint8Array(input.length + paddingLength);
        output.set(input);
        output[input.length] = 0x80;
        return output;
    },
    unpad(input, blockSize) {
        assertPaddedInput(input, blockSize);
        let index = input.length - 1;
        while (index >= 0 && input[index] === 0) {
            index--;
        }
        if (index < 0 || input[index] !== 0x80) {
            throw new Error('Invalid ISO/IEC 9797-1 padding.');
        }
        return input.slice(0, index);
    },
};
const iso97971Preset = {
    kind: 'preset',
    name: 'iso97971',
    components() {
        return [iso97971];
    },
};
function assertBlockSize(blockSize) {
    assertBlockSize$3(blockSize);
}
function assertPaddedInput(input, blockSize) {
    assertPaddedInput$3(input, blockSize, 'ISO/IEC 9797-1');
}
function getPaddingLength(inputLength, blockSize) {
    const remainder = (inputLength + 1) % blockSize;
    return 1 + (remainder === 0 ? 0 : blockSize - remainder);
}

const noPadding = {
    kind: 'padding',
    name: 'NoPadding',
    pad(input, blockSize) {
        assertMultiple(input, blockSize);
        return input.slice();
    },
    unpad(input, blockSize) {
        assertMultiple(input, blockSize);
        return input.slice();
    },
};
const noPaddingPreset = {
    kind: 'preset',
    name: 'none',
    components() {
        return [noPadding];
    },
};
function assertMultiple(input, blockSize) {
    assertBlockMultiple(input, blockSize, 'NoPadding');
}

const pkcs7 = {
    kind: 'padding',
    name: 'Pkcs7',
    pad(input, blockSize) {
        assertBlockSize$3(blockSize, { max: 255 });
        const paddingLength = getBlockPaddingLength(input.length, blockSize);
        const output = new Uint8Array(input.length + paddingLength);
        output.set(input);
        output.fill(paddingLength, input.length);
        return output;
    },
    unpad(input, blockSize) {
        assertPaddedInput$3(input, blockSize, 'PKCS#7', { max: 255 });
        const paddingLength = input[input.length - 1];
        if (paddingLength === 0 || paddingLength > blockSize || paddingLength > input.length) {
            throw new Error('Invalid PKCS#7 padding.');
        }
        for (let i = input.length - paddingLength; i < input.length; i++) {
            if (input[i] !== paddingLength) {
                throw new Error('Invalid PKCS#7 padding.');
            }
        }
        return input.slice(0, input.length - paddingLength);
    },
};
const pkcs7Preset = {
    kind: 'preset',
    name: 'pkcs7',
    components() {
        return [pkcs7];
    },
};

const zeroPadding = {
    kind: 'padding',
    name: 'ZeroPadding',
    pad(input, blockSize) {
        if (!Number.isInteger(blockSize) || blockSize <= 0) {
            throw new RangeError('blockSize must be a positive integer.');
        }
        const remainder = input.length % blockSize;
        if (remainder === 0) {
            return input.slice();
        }
        const output = new Uint8Array(input.length + blockSize - remainder);
        output.set(input);
        return output;
    },
    unpad(input) {
        let end = input.length;
        while (end > 0 && input[end - 1] === 0) {
            end--;
        }
        return input.slice(0, end);
    },
};
const zeroPaddingPreset = {
    kind: 'preset',
    name: 'zero',
    components() {
        return [zeroPadding];
    },
};

function paddingsPreset() {
    return {
        kind: 'preset',
        name: 'paddings',
        components() {
            return [pkcs7Preset, noPaddingPreset, ansiX923Preset, iso10126Preset, iso97971Preset, zeroPaddingPreset];
        },
    };
}

export { ansiX923, ansiX923Preset, iso10126, iso10126Preset, iso97971, iso97971Preset, noPadding, noPaddingPreset, paddingsPreset, pkcs7, pkcs7Preset, zeroPadding, zeroPaddingPreset };
//# sourceMappingURL=index.mjs.map
